import React, {
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
  useCallback,
  useMemo,
} from 'react'
import { Stage, Layer } from 'react-konva'
import type Konva from 'konva'
import { loadSofaElement, type SofaElementProps } from './DynamicLoadSofaShape'
import type {
  BlueprintGutters,
  BlueprintLayout,
} from './blueprint-overlay/types'

export type { BlueprintLayout } from './blueprint-overlay/types'

const DEFAULT_FILL_GUTTERS: BlueprintGutters = {
  top: 36,
  right: 84,
  bottom: 40,
  left: 76,
}
const DEFAULT_FILL_HEIGHT = 460
// Fill mode: share of the inner box (container minus gutters) the shape may
// occupy along its limiting axis, and the max zoom (px per cm). Tweak here or
// per call via the `fillOccupancy` / `maxFillScale` props.
export const DEFAULT_FILL_OCCUPANCY = 0.6
export const DEFAULT_MAX_FILL_SCALE = 2

interface SofaBlueprintProps {
  id: number
  containerHeight?: string
  containerWidth?: string
  dimensions: {
    width?: number | null
    length?: number | null
    armrest_width?: number | null
    backrest_width?: number | null
    mattress_width?: number | null
    mattress_length?: number | null
    corner_part_length?: number | null
    angle?: number | null
    seat_height?: number | null
    extendable_part_length?: number | null
    number_of_big_pillows?: number | null
    number_of_small_pillows?: number | null
    spread_of_big_pillows?: number | null
    spread_of_small_pillows?: number | null
    size_of_big_pillow?: number | null
    size_of_pillow?: number | null
    corner_radius?: number | null
    seat_sections?: number | null
    backrest_sections?: number | null
    extension_type?: string | null
    backrest_type?: string | null
    covered_side?: boolean | null
    // Add any other properties that might be needed
    // For example, if you have a composition property:
    composition?: Array<{
      shape: string
      width: number
      height: number
      corner_part_length?: number
    }> | null
  }
  type: string
  showMetrics?: boolean
  onCompositionExtentChange?: (extent: { width: number; height: number }) => void
  /**
   * 'legacy' (default) keeps the historical sizing: scale only down, ×0.9,
   * container height = length + 150px. 'fill' always scales the shape to fit
   * the box minus `gutters`, scaling up as well — used by the on-blueprint
   * dimension editor.
   */
  fit?: 'legacy' | 'fill'
  /** Fill mode only: px kept free around the shape for dimension lines. */
  gutters?: Partial<BlueprintGutters>
  /** Fill mode only: exact container/Stage height in px. */
  height?: number
  /** Fill mode only: 0–1 share of the inner box the shape may fill. */
  fillOccupancy?: number
  /** Fill mode only: cap on scale-up (px per cm). */
  maxFillScale?: number
  /**
   * Rendered as an absolutely positioned sibling over the Stage. Setting it
   * hides the shape's own Konva metric lines (the overlay draws its own).
   */
  renderOverlay?: (layout: BlueprintLayout) => React.ReactNode
  /** Called whenever the measured overlay layout changes (renderOverlay only). */
  onLayoutChange?: (layout: BlueprintLayout) => void
}

const sameLayout = (a: BlueprintLayout | null, b: BlueprintLayout) =>
  !!a &&
  a.ready === b.ready &&
  a.type === b.type &&
  a.scale === b.scale &&
  a.armrestPosition === b.armrestPosition &&
  a.extension?.x === b.extension?.x &&
  a.extension?.y === b.extension?.y &&
  a.extension?.width === b.extension?.width &&
  a.extension?.height === b.extension?.height &&
  a.container.width === b.container.width &&
  a.container.height === b.container.height &&
  a.shapeRect.x === b.shapeRect.x &&
  a.shapeRect.y === b.shapeRect.y &&
  a.shapeRect.width === b.shapeRect.width &&
  a.shapeRect.height === b.shapeRect.height

export default function SofaBlueprint({
  id,
  dimensions,
  type,
  containerHeight,
  containerWidth = '100%',
  showMetrics = false,
  onCompositionExtentChange,
  fit = 'legacy',
  gutters,
  height,
  fillOccupancy = DEFAULT_FILL_OCCUPANCY,
  maxFillScale = DEFAULT_MAX_FILL_SCALE,
  renderOverlay,
  onLayoutChange,
}: SofaBlueprintProps) {
  const [stageSize, setStageSize] = useState({ width: 400, height: 600 })
  const [SofaElement, setSofaElement] =
    useState<React.ComponentType<SofaElementProps> | null>(null)
  const [loadedType, setLoadedType] = useState<string | null>(null)
  const [layout, setLayout] = useState<BlueprintLayout | null>(null)
  // Fill mode: offset (cm, group-local) of the drawn content inside the root
  // group. Non-zero only for COMPOSITE, whose members sit at an offset.
  const [contentOffset, setContentOffset] = useState({ x: 0, y: 0 })
  // Fill mode: cm drawn below the seat (extendable part), included when
  // fitting so the extension isn't pushed out of the box.
  const [extraBottomCm, setExtraBottomCm] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const layerRef = useRef<Konva.Layer>(null)

  const isFill = fit === 'fill'
  const fillHeight = height ?? DEFAULT_FILL_HEIGHT
  const gTop = gutters?.top ?? DEFAULT_FILL_GUTTERS.top
  const gRight = gutters?.right ?? DEFAULT_FILL_GUTTERS.right
  const gBottom = gutters?.bottom ?? DEFAULT_FILL_GUTTERS.bottom
  const gLeft = gutters?.left ?? DEFAULT_FILL_GUTTERS.left
  const fillGutters = useMemo(
    () => ({ top: gTop, right: gRight, bottom: gBottom, left: gLeft }),
    [gTop, gRight, gBottom, gLeft]
  )

  // Calculate dynamic container height based on shape height + 60px for labels
  const shapeHeight = dimensions?.length || 100
  const dynamicContainerHeight = isFill
    ? `${fillHeight}px`
    : containerHeight || `${shapeHeight + 150}px`

  const updateDimensions = useCallback(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect()
      setStageSize({ width, height })
    }
  }, [])

  useEffect(() => {
    updateDimensions()
    window.addEventListener('resize', updateDimensions)
    return () => window.removeEventListener('resize', updateDimensions)
  }, [updateDimensions])

  // Fill mode lives in drawers whose width settles after the open animation;
  // track the container itself, not just the window.
  useEffect(() => {
    if (!isFill || !containerRef.current || typeof ResizeObserver === 'undefined')
      return
    const observer = new ResizeObserver(() => updateDimensions())
    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [isFill, updateDimensions])

  useEffect(() => {
    let cancelled = false
    const loadShape = async () => {
      try {
        const moduleExports = await loadSofaElement(type)
        if (cancelled) return
        if (moduleExports?.default) {
          setSofaElement(() => moduleExports.default)
          setLoadedType(type)
        }
      } catch (error) {
        console.error('Error loading sofa element:', error)
      }
    }
    loadShape()
    return () => {
      cancelled = true
    }
  }, [type])

  // Memoize calculated values to optimize re-renders
  const calculatedValues = useMemo(() => {
    const shapeWidth = dimensions?.width || 200

    if (isFill) {
      const innerW = Math.max(
        1,
        stageSize.width - fillGutters.left - fillGutters.right
      )
      const innerH = Math.max(1, fillHeight - fillGutters.top - fillGutters.bottom)
      const fitHeight = shapeHeight + extraBottomCm
      const fillScale = Math.min(
        (innerW / shapeWidth) * fillOccupancy,
        (innerH / fitHeight) * fillOccupancy,
        maxFillScale
      )
      return {
        shapeWidth,
        adjustedWidth: stageSize.width,
        adjustedHeight: fillHeight,
        scale: fillScale,
        innerW,
        innerH,
      }
    }

    const adjustedWidth = stageSize.width
    const adjustedHeight = stageSize.height // Adjust height to leave space for labels

    // Calculate scale only if shape is larger than container
    const needsScaling =
      shapeWidth > adjustedWidth || shapeHeight > adjustedHeight
    const scale = needsScaling
      ? Math.min(adjustedWidth / shapeWidth, adjustedHeight / shapeHeight)
      : 1

    return {
      shapeWidth,
      adjustedWidth,
      adjustedHeight,
      scale: scale * 0.9,
      innerW: 0,
      innerH: 0,
    }
  }, [
    dimensions?.width,
    shapeHeight,
    stageSize.width,
    stageSize.height,
    isFill,
    fillHeight,
    fillGutters,
    fillOccupancy,
    maxFillScale,
    extraBottomCm,
  ])

  const { shapeWidth, adjustedWidth, adjustedHeight, scale, innerW, innerH } =
    calculatedValues

  // Overlay support: read the drawn shape's rect back from the Konva tree.
  // Uses the root group's own x/y/width/height attrs rather than
  // getClientRect, which would also include connector gizmos, metric lines
  // and pillow overflow. COMPOSITE is measured from its members group.
  const measure = useCallback(() => {
    if (!renderOverlay) return
    const group = layerRef.current?.getChildren()[0] as Konva.Group | undefined
    const container = { width: stageSize.width, height: fillHeight }
    if (!group || !SofaElement) {
      const next: BlueprintLayout = {
        scale,
        container,
        shapeRect: { x: 0, y: 0, width: 0, height: 0 },
        armrestPosition: '',
        extension: null,
        type,
        ready: false,
      }
      setLayout(prev => (sameLayout(prev, next) ? prev : next))
      return
    }
    const round = (n: number) => Math.round(n * 100) / 100
    let local = { x: 0, y: 0, width: group.width(), height: group.height() }
    const members = group.findOne('.composite_members')
    if (members) {
      const r = members.getClientRect({
        relativeTo: group,
        skipStroke: true,
        skipShadow: true,
      })
      if (r.width > 0 && r.height > 0) {
        local = {
          x: Math.round(r.x),
          y: Math.round(r.y),
          width: Math.round(r.width),
          height: Math.round(r.height),
        }
      }
    }
    // Extendable part: shapes draw it as Rects directly under the seat
    // (y >= group height), e.g. the dashed pull-out or recliner sections.
    // Their own "N cm" metric for it ignores the verticalMetric prop, so hide
    // it here; the overlay draws an editable line instead. Read-only w.r.t.
    // the shape files (portal-synced), only Konva node visibility changes.
    let extension: BlueprintLayout['extension'] = null
    if (!members) {
      const seatH = group.height()
      let minX = Infinity
      let maxX = -Infinity
      let maxY = -Infinity
      let hidMetric = false
      group.getChildren().forEach(child => {
        if (child.y() < seatH - 1) return
        if (child.getClassName() === 'Rect') {
          const rect = child as Konva.Rect
          if (rect.height() <= 0 || rect.width() <= 0) return
          minX = Math.min(minX, rect.x())
          maxX = Math.max(maxX, rect.x() + rect.width())
          maxY = Math.max(maxY, rect.y() + rect.height())
        } else if (
          child.getClassName() === 'Group' &&
          (child as Konva.Group)
            .find('Text')
            .some(t => /\bcm$/.test((t as Konva.Text).text()))
        ) {
          if (child.visible()) {
            child.visible(false)
            hidMetric = true
          }
        }
      })
      if (hidMetric) layerRef.current?.batchDraw()
      const extraCm =
        maxY > seatH + 0.5 && Number.isFinite(minX)
          ? Math.round(maxY - seatH)
          : 0
      setExtraBottomCm(prev => (prev === extraCm ? prev : extraCm))
      if (extraCm > 0) {
        extension = {
          x: round((group.x() + minX) * scale),
          y: round((group.y() + seatH) * scale),
          width: round((maxX - minX) * scale),
          height: round((maxY - seatH) * scale),
        }
      }
    }
    setContentOffset(prev =>
      prev.x === local.x && prev.y === local.y
        ? prev
        : { x: local.x, y: local.y }
    )
    const next: BlueprintLayout = {
      scale,
      container,
      shapeRect: {
        x: round((group.x() + local.x) * scale),
        y: round((group.y() + local.y) * scale),
        width: round(local.width * scale),
        height: round(local.height * scale),
      },
      armrestPosition: String(group.getAttr('armrestPosition') ?? ''),
      extension,
      type,
      ready: loadedType === type,
    }
    setLayout(prev => (sameLayout(prev, next) ? prev : next))
  }, [renderOverlay, stageSize.width, fillHeight, scale, type, loadedType, SofaElement])

  // Runs after every render (react-konva commits the shape tree in the
  // Stage's own layout effect, before ours). Equality guards stop loops.
  useLayoutEffect(() => {
    if (!renderOverlay) return
    measure()
    // COMPOSITE measures its members in its own effect; catch the settled
    // geometry a frame later.
    const raf = requestAnimationFrame(measure)
    return () => cancelAnimationFrame(raf)
  })

  useEffect(() => {
    if (layout) onLayoutChange?.(layout)
  }, [layout, onLayoutChange])

  const handleExtentChange = useCallback(
    (extent: { width: number; height: number }) => {
      onCompositionExtentChange?.(extent)
      if (renderOverlay) requestAnimationFrame(measure)
    },
    [onCompositionExtentChange, renderOverlay, measure]
  )

  // Force re-render when dimensions change
  useEffect(() => {
    // This effect ensures the component updates when dimensions change
    // The dependency array will trigger re-renders for any dimension changes
  }, [dimensions])

  return (
    <div
      ref={containerRef}
      className=" p-0 bg-white"
      style={{
        height: dynamicContainerHeight,
        width: containerWidth,
        position: 'relative',
      }}
    >
      <Stage
        width={stageSize.width}
        height={isFill ? fillHeight : stageSize.height * 2}
        style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none' }}
        scale={{ x: scale, y: scale }}
      >
        <Layer ref={layerRef}>
          {SofaElement &&
            (() => {
              let x: number
              let y: number
              if (isFill) {
                x =
                  (fillGutters.left + (innerW - shapeWidth * scale) / 2) /
                    scale -
                  contentOffset.x
                y =
                  (fillGutters.top +
                    (innerH - (shapeHeight + extraBottomCm) * scale) / 2) /
                    scale -
                  contentOffset.y
              } else {
                // Center calculation
                const shapeCenterX = shapeWidth / 2
                const shapeCenterY = shapeHeight / 2 + 0
                const stageCenterX = adjustedWidth / 2
                const stageCenterY = adjustedHeight / 2
                x = stageCenterX / scale - shapeCenterX
                y = stageCenterY / scale - shapeCenterY
              }

              return (
                <SofaElement
                  id={id.toString()}
                  width={shapeWidth}
                  height={shapeHeight}
                  x={x}
                  y={y}
                  scale={scale}
                  stageWidth={stageSize.width}
                  stageHeight={stageSize.height}
                  armrestWidth={dimensions?.armrest_width ?? undefined}
                  backrestWidth={dimensions?.backrest_width ?? undefined}
                  mattressWidth={dimensions?.mattress_width ?? undefined}
                  mattressLength={dimensions?.mattress_length ?? undefined}
                  cornerPartLength={dimensions?.corner_part_length ?? undefined}
                  currentRotation={0}
                  rotation={0}
                  angle={dimensions?.angle ?? undefined}
                  verticalMetric={showMetrics && !renderOverlay}
                  horizontalMetric={showMetrics && !renderOverlay}
                  composition={dimensions?.composition ?? undefined}
                  cornerRadius={dimensions?.corner_radius ?? undefined}
                  extendablePartLength={dimensions.extendable_part_length}
                  numberOfBigPillows={dimensions.number_of_big_pillows}
                  numberOfSmallPillows={dimensions.number_of_small_pillows}
                  spreadOfBigPillows={dimensions.spread_of_big_pillows}
                  spreadOfSmallPillows={dimensions.spread_of_small_pillows}
                  sizeOfBigPillow={dimensions.size_of_big_pillow}
                  sizeOfPillow={dimensions.size_of_pillow}
                  seatSections={dimensions.seat_sections}
                  backrestSections={dimensions.backrest_sections}
                  extensionType={dimensions.extension_type}
                  backrestType={dimensions.backrest_type}
                  coveredSide={dimensions.covered_side}
                  onExtentChange={
                    type === 'COMPOSITE'
                      ? renderOverlay
                        ? handleExtentChange
                        : onCompositionExtentChange
                      : undefined
                  }
                />
              )
            })()}
        </Layer>
      </Stage>
      {renderOverlay && layout && (
        <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}>
          {renderOverlay(layout)}
        </div>
      )}
    </div>
  )
}
